import { useState, useEffect, useCallback, useRef } from 'react';
import pkceService from '../services/pkceService';
import debugPKCE from '../utils/debugPKCE';
import tenantService from '../services/tenantService';
import * as deviceStore from '../db/deviceStore';
import API_BASE_URL, { HOME_ROUTE } from '../config';
import { getAuthHeaders as getAuthHeadersUtil } from '../utils/authHeaders';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { runAuthContextDeduped } from '../utils/authContextDedupe';
import { clearAuthTokens, getAccessToken, getUserInfoRaw } from '../utils/tokenStorage';
import { refreshAccessToken } from '../utils/tokenRefresh';
import { initProactiveRefresh, cancelProactiveRefresh } from '../utils/proactiveTokenRefresh';
import hazardService from '../services/hazardService';
import * as hazardGateway from '../db/hazardGateway';

const isDevEnvironment = process.env.NODE_ENV !== 'production';

interface UserInfo {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
}

interface OidcConfig {
  id: number;
  clientId: string;
  tenantId: string;
  authority: string;
  redirectUri: string;
  scope: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  requirePkce: boolean;
  codeChallengeMethod: string;
  // Add discovered endpoints
  authorizationEndpoint?: string;
  tokenEndpoint?: string;
  userinfoEndpoint?: string;
  endSessionEndpoint?: string;
  jwksUri?: string;
  issuer?: string;
  discoveryUrl?: string;
}

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [oidcConfig, setOidcConfig] = useState<OidcConfig | null>(null);
  const [oidcConfigLoaded, setOidcConfigLoaded] = useState<boolean>(false);
  const [isLoginInProgress, setIsLoginInProgress] = useState<boolean>(false);

  // Use centralized auth headers utility
  // Note: We still expose getAuthHeaders from useAuth for backward compatibility
  // but it now uses the centralized utility with mock auth support
  const getAuthHeaders = useCallback((): Record<string, string> => {
    // Use centralized utility which handles mock auth for tests
    return getAuthHeadersUtil();
  }, []);

  // Function to add user to database (only called once per session)
  const addUserToDatabase = useCallback(async (userId: string, username: string, email?: string) => {
    try {
      const userEnsuredKey = `user_ensured_${userId}`;

      if (sessionStorage.getItem(userEnsuredKey) === 'true') {
        console.log('✅ User existence already ensured in this session, skipping check');
        return;
      }

      await runAuthContextDeduped(`ensureUser:${userId}`, async () => {
        if (sessionStorage.getItem(userEnsuredKey) === 'true') {
          return;
        }

        const headers = getAuthHeaders();

        // Route through fetchWithAuth so this initial-load probe gets timeout +
        // bounded retry on transient network errors (not raw fetch).
        const checkResponse = await fetchWithAuth(`${API_BASE_URL}/api/getUserWithRoles`, {
          method: 'GET',
          headers,
        });

        if (checkResponse.ok) {
          console.log('✅ User already exists in database, skipping addUser');
          sessionStorage.setItem(userEnsuredKey, 'true');
          return;
        }

        if (checkResponse.status === 404) {
          console.log('👤 User not found in DB, creating user...');
          const addResponse = await fetchWithAuth(`${API_BASE_URL}/api/addUser`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              userId,
              username,
              email,
            }),
          });

          if (addResponse.ok) {
            const result = await addResponse.json();
            console.log('✅ User successfully added to database:', result);
            sessionStorage.setItem(userEnsuredKey, 'true');
          } else {
            const error = await addResponse.json().catch(() => ({ error: { message: 'Unknown error' } }));
            console.warn('⚠️ Failed to add user to database:', addResponse.status, error);
          }
        } else {
          console.warn('⚠️ Unexpected response when checking user existence:', checkResponse.status);
        }
      });
    } catch (error) {
      console.error('Error adding user to database:', error);
    }
  }, [getAuthHeaders]);

  // Function to fetch and store tenant info in local database
  const fetchAndStoreTenantInfo = useCallback(async () => {
    try {
      console.log('Fetching tenant information...');
      const tenantInfo = await tenantService.getCurrentTenant();
      
      if (tenantInfo) {
        // Store tenant info in local database
        const tenantInfoWithTimestamp = {
          ...tenantInfo,
          last_updated: new Date().toISOString()
        };
        
        await deviceStore.putTenantInfo(tenantInfoWithTimestamp);
        console.log('Tenant information stored in local database:', tenantInfoWithTimestamp);
      } else {
        console.warn('No tenant information received from server');
      }
    } catch (error) {
      console.error('Error fetching and storing tenant information:', error);
    }
  }, []);

  // Function to fetch OIDC configuration
  const fetchOidcConfig = useCallback(async (): Promise<OidcConfig | null> => {
    // Skip if already successfully loaded
    if (oidcConfigLoaded && oidcConfig) {
      console.log('OIDC configuration already loaded, skipping fetch');
      return oidcConfig;
    }

    // Try to load from localStorage cache first
    const cachedConfig = localStorage.getItem('oidc_config_cache');
    if (cachedConfig) {
      try {
        const parsedConfig = JSON.parse(cachedConfig);
        console.log('✅ Using cached OIDC configuration:', parsedConfig.clientId);
        setOidcConfig(parsedConfig);
        setOidcConfigLoaded(true);
        return parsedConfig;
      } catch (error) {
        console.warn('Failed to parse cached OIDC config, will fetch fresh:', error);
        localStorage.removeItem('oidc_config_cache');
      }
    }

    console.log('Fetching OIDC configuration...');
    
    // Retry logic for important config loading
    const maxRetries = 3;
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/getOidcConfig`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(typeof window !== 'undefined'
              ? { 'X-Forwarded-Host': window.location.host }
              : {}),
          },
        });
        
        if (response.ok) {
          const configs = await response.json();
          if (configs && configs.length > 0) {
            const enabledConfig = configs.find((config: OidcConfig) => config.enabled);
            if (enabledConfig) {
              setOidcConfig(enabledConfig);
              setOidcConfigLoaded(true); // Only set to true when successful
              
              // Cache the configuration for future use
              localStorage.setItem('oidc_config_cache', JSON.stringify(enabledConfig));
              console.log('OIDC configuration loaded and cached:', enabledConfig.clientId);
              return enabledConfig;
            } else {
              console.warn('No enabled OIDC configuration found');
            }
          } else {
            console.warn('No OIDC configurations returned');
          }
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        console.warn(`OIDC config fetch attempt ${attempt}/${maxRetries} failed:`, lastError.message);
        
        // Wait before retry (except for last attempt)
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
    
    console.error('Failed to fetch OIDC configuration after', maxRetries, 'attempts:', lastError?.message);
    
    // Don't set oidcConfigLoaded to true if we failed to load config
    // This allows for retries
    return null;
  }, [oidcConfigLoaded, oidcConfig]);

  // Function to validate OAuth state parameter
  const validateState = (receivedState: string): boolean => {
    const storedState = sessionStorage.getItem('oauth_state');
    sessionStorage.removeItem('oauth_state'); // Clean up
    return storedState === receivedState;
  };

  const isCheckingAuthRef = useRef<boolean>(false);
  
  const checkAuth = useCallback(async () => {
    // Prevent overlapping authentication checks
    if (isCheckingAuthRef.current) {
      console.log('⏳ Authentication check already in progress, skipping...');
      return isAuthenticated;
    }
    
    // Quick token check first (before any state changes)
    const oidcUserInfo = getUserInfoRaw();
    const oidcAccessToken = getAccessToken();
    
    // If we're already authenticated and tokens still exist, return early without any state changes
    if (isAuthenticated && oidcUserInfo && oidcAccessToken) {
      console.log('✅ Already authenticated with valid tokens, skipping full check');
      return true;
    }
    
    console.log('🔍 Checking authentication...');
    isCheckingAuthRef.current = true;
    
    // Only set loading state if we're not already authenticated or tokens are missing
    const needsFullCheck = !isAuthenticated || !oidcUserInfo || !oidcAccessToken;
    if (needsFullCheck) {
      setIsLoading(true);
    }
    
    let authenticationResult = false;
    try {
      // Tokens already retrieved above
      console.log('OIDC tokens check:', {
        hasUserInfo: !!oidcUserInfo,
        hasAccessToken: !!oidcAccessToken,
      });
      
      if (oidcUserInfo && oidcAccessToken) {
        // User is authenticated via OIDC
        const userInfo = JSON.parse(oidcUserInfo);
        console.log('OIDC authentication found for user:', userInfo.userId);
        setIsAuthenticated(true);
        setUserInfo(userInfo);
        authenticationResult = true;
        
        // Ensure user exists in database (for page refreshes and direct navigation)
        if (userInfo && userInfo.userId && userInfo.userDetails) {
          console.log('🔍 Ensuring user exists in database...');
          addUserToDatabase(userInfo.userId, userInfo.userDetails, userInfo.email).catch(error => {
            console.error('Failed to ensure user exists in database:', error);
          });
        }

        // Fetch and store tenant info in the background
        fetchAndStoreTenantInfo().catch(error => {
          console.error('Failed to fetch and store tenant info during login:', error);
        });
      } else {
        console.log('❌ No OIDC authentication found');
        setIsAuthenticated(false);
        setUserInfo(null);
        authenticationResult = false;
      }
    } catch (error) {
      console.error('💥 Error checking authentication:', error);
      setIsAuthenticated(false);
      setUserInfo(null);
      authenticationResult = false;
    } finally {
      console.log('🏁 Authentication check complete, isAuthenticated:', authenticationResult);
      // Only set loading to false if we set it to true earlier
      if (needsFullCheck) {
        setIsLoading(false);
      }
      isCheckingAuthRef.current = false;
    }
    return authenticationResult;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUserToDatabase, fetchAndStoreTenantInfo]);

  useEffect(() => {
    const handleOidcAuthExpired = () => {
      console.log('🔒 OIDC session expired - clearing auth state');
      setIsAuthenticated(false);
      setUserInfo(null);
      setIsLoading(false);
    };

    const handleOidcLoginSuccess = () => {
      console.log('🎊 OIDC login success event received - rechecking authentication');
      // Only check auth if not already checking and not already authenticated
      if (!isCheckingAuthRef.current && !isAuthenticated) {
        checkAuth().then((authResult) => {
          if (authResult) {
            console.log('🚀 Authentication confirmed, triggering navigation');
            // Force navigation to authenticated route
            const currentPath = window.location.pathname;
            if (currentPath === '/login' || currentPath === '/auth/callback') {
              console.log('🔄 Redirecting from', currentPath, 'to', HOME_ROUTE);
              window.location.href = HOME_ROUTE;
            }
          }
        });
      } else if (isAuthenticated) {
        console.log('🎯 Already authenticated, skipping login success handler');
      } else {
        console.log('⏳ Authentication check already in progress, skipping login success handler');
      }
    };

    // Prioritize OIDC config loading for callback scenarios
    const currentPath = window.location.pathname;
    const isCallbackUrl = currentPath === '/auth/callback';
    
    if (isCallbackUrl) {
      console.log('🔄 Callback URL detected, prioritizing OIDC config loading...');
      // For callback URLs, load config immediately and synchronously
      fetchOidcConfig().then((config) => {
        if (config) {
          console.log('✅ OIDC config loaded for callback scenario');
        } else {
          console.error('❌ Failed to load OIDC config for callback scenario');
        }
      }).catch(error => {
        console.error('💥 Error loading OIDC config for callback:', error);
      });
    } else {
      // For non-callback scenarios, load config in background
      fetchOidcConfig().catch(error => {
        console.error('Failed to fetch OIDC config on mount:', error);
      });
    }

    // Initial authentication check (only if not already authenticated)
    if (!isAuthenticated) {
      checkAuth();
    }

    // Initialize proactive token refresh (re-schedules based on stored expiry)
    initProactiveRefresh();
    
    // Listen for OIDC login success and session expiry events
    window.addEventListener('oidc-login-success', handleOidcLoginSuccess);
    window.addEventListener('oidc-auth-expired', handleOidcAuthExpired);
    
    return () => {
      window.removeEventListener('oidc-login-success', handleOidcLoginSuccess);
      window.removeEventListener('oidc-auth-expired', handleOidcAuthExpired);
    };
    // Only run this effect once on mount, not when dependencies change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async () => {
    // Prevent duplicate login attempts
    if (isLoginInProgress) {
      console.log('⏳ Login already in progress, ignoring duplicate request');
      return;
    }
    
    console.log('🚀 Starting login process...');
    
    // Use OIDC authentication for Microsoft account only
    setIsLoginInProgress(true);
    try {
      await initiateOIDCLogin();
      console.log('✅ Login initiation completed successfully');
      // If we reach here, initiateOIDCLogin succeeded and should redirect
      // No need to reset login state as page will redirect
    } catch (error) {
      console.error('❌ Login failed:', error);
      // Reset login state immediately on error
      setIsLoginInProgress(false);
    }
  };

  const initiateOIDCLogin = async () => {
    try {
      // Get or fetch OIDC configuration
      let config = oidcConfig;
      
      if (!config) {
        console.log('⏰ OIDC configuration not available, fetching...');
        config = await fetchOidcConfig();
      }
      
      if (!config) {
        console.error('❌ Failed to load OIDC configuration');
        alert('Authentication configuration not available. Please try again.');
        // Reset login state immediately so user can try again
        setIsLoginInProgress(false);
        return;
      }

      console.log('OIDC configuration available, proceeding with login');
      if (isDevEnvironment) {
        debugPKCE.logOIDCConfig(config);
      }

      // Generate PKCE parameters if required
      let codeChallenge = '';
      if (config.requirePkce) {
        if (!pkceService.isPKCESupported()) {
          console.error('PKCE is required but not supported by browser');
          setIsLoginInProgress(false);
          return;
        }

        const { codeChallenge: challenge } = await pkceService.generateAndStorePKCEParameters();
        codeChallenge = challenge;
        if (isDevEnvironment) {
          debugPKCE.logPKCEState();
        }
      }

      // Build authorization URL
      const authUrl = buildAuthorizationUrl(config, codeChallenge);
      console.log('Redirecting to OIDC authorization URL:', authUrl);
      
      // Redirect to authorization server
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error initiating OIDC login:', error);
      setIsLoginInProgress(false);
    }
  };

  const buildAuthorizationUrl = (config: OidcConfig, codeChallenge?: string): string => {
    // Use discovered endpoint or fallback to Microsoft format
    const authEndpoint = config.authorizationEndpoint || 
      `${config.authority}/oauth2/v2.0/authorize`;
    
    const authUrl = new URL(authEndpoint);
    
    // Construct redirect URI with current protocol and domain
    const redirectUri = `${window.location.protocol}//${window.location.host}${config.redirectUri}`;
    
    // Required parameters
    authUrl.searchParams.set('client_id', config.clientId);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('scope', config.scope);
    
    // Add state parameter for security
    const state = generateRandomState();
    authUrl.searchParams.set('state', state);
    sessionStorage.setItem('oauth_state', state);
    
    // Add PKCE parameters if required
    if (config.requirePkce && codeChallenge) {
      authUrl.searchParams.set('code_challenge', codeChallenge);
      authUrl.searchParams.set('code_challenge_method', config.codeChallengeMethod || 'S256');
    }
    
    return authUrl.toString();
  };

  const generateRandomState = (): string => {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const confirmPendingLogout = (): Promise<'sync' | 'logout' | 'cancel'> => {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.setAttribute('role', 'presentation');
      overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.45);z-index:10000;display:flex;align-items:center;justify-content:center;';
      const dialog = document.createElement('div');
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-labelledby', 'pending-outbox-title');
      dialog.style.cssText = 'background:#fff;color:#111;max-width:24rem;margin:1rem;padding:1.25rem;border-radius:0.75rem;box-shadow:0 10px 30px rgba(0,0,0,0.2);';
      const title = document.createElement('h2');
      title.id = 'pending-outbox-title';
      title.textContent = 'Unsynced changes';
      title.style.cssText = 'margin:0 0 0.5rem;font-size:1.125rem;';
      const body = document.createElement('p');
      body.textContent = 'This device still has hazard changes that have not reached the server.';
      body.style.cssText = 'margin:0 0 1rem;';
      const actions = document.createElement('div');
      actions.style.cssText = 'display:flex;gap:0.5rem;justify-content:flex-end;';
      const syncBtn = document.createElement('button');
      syncBtn.type = 'button';
      syncBtn.textContent = 'Sync now';
      const logoutBtn = document.createElement('button');
      logoutBtn.type = 'button';
      logoutBtn.textContent = 'Log out anyway';
      const finish = (choice: 'sync' | 'logout' | 'cancel') => {
        document.removeEventListener('keydown', onKey);
        overlay.remove();
        resolve(choice);
      };
      const onKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          finish('cancel');
        }
      };
      syncBtn.addEventListener('click', () => finish('sync'));
      logoutBtn.addEventListener('click', () => finish('logout'));
      overlay.addEventListener('click', (event) => {
        if (event.target === overlay) {
          finish('cancel');
        }
      });
      document.addEventListener('keydown', onKey);
      actions.append(syncBtn, logoutBtn);
      dialog.append(title, body, actions);
      overlay.append(dialog);
      document.body.append(overlay);
      syncBtn.focus();
    });
  };

  const logout = async () => {
    try {
      if (await hazardGateway.hasPending()) {
        const choice = await confirmPendingLogout();
        if (choice === 'cancel') {
          return;
        }
        if (choice === 'sync') {
          await hazardService.pushUnsyncedHazards();
          if (await hazardGateway.hasPending()) {
            return;
          }
        }
      }
      await hazardGateway.deleteOpenUserStore();
    } catch (error) {
      console.error('Logout could not finish the local store step:', error);
      return;
    }

    cancelProactiveRefresh();

    // Clear OIDC tokens (centralized; refresh token may be in sessionStorage)
    clearAuthTokens();
    
    // Clear cached OIDC configuration
    localStorage.removeItem('oidc_config_cache');
    
    // Invalidate cached tenant info
    try {
      tenantService.invalidateCache();
    } catch (_) {
      // ignore
    }
    
    // Clear PKCE parameters
    sessionStorage.removeItem('pkce_code_verifier');
    sessionStorage.removeItem('pkce_code_challenge');
    sessionStorage.removeItem('oauth_state');
    
    // Clear user existence cache
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('user_ensured_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    // Use discovered logout endpoint or fallback
    if (oidcConfig) {
      const logoutEndpoint = oidcConfig.endSessionEndpoint || 
        `${oidcConfig.authority}/oauth2/v2.0/logout`;
      
      const logoutUrl = `${logoutEndpoint}?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
      window.location.href = logoutUrl;
    } else {
      // Fallback to home page
      window.location.href = '/';
    }
  };

  // Expose checkAuth function for external use; also attempts OIDC token refresh
  const refreshAuth = useCallback(async () => {
    console.log('🔄 Manually refreshing authentication state...');
    const tokenRefreshed = await refreshAccessToken();
    if (tokenRefreshed) {
      console.log('✅ Access token refreshed via OIDC');
    }
    const result = await checkAuth();
    console.log('✅ Auth refresh completed, result:', result);
    return result;
  }, [checkAuth]);

  return {
    isAuthenticated,
    userInfo,
    isLoading,
    isLoginInProgress,
    login,
    logout,
    oidcConfig,
    validateState,
    refreshAuth,
    getAuthHeaders
  };
}; 